import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { ReactNode, useEffect, useState } from "react";

import { TextField } from "atoms/text-field/TextField";
import { formatToLongDate } from "utils/dayjsUtils";
import { isValidDateFormat } from "utils/validators";

// Initialize dayjs plugins
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

// Ensure dayjs has a default locale set for formatToLongDate to work
if (!dayjs.locale() || dayjs.locale() === "en") {
  dayjs.locale("en");
}

export interface DateOfBirthFieldProps {
  /** The current value as ISO date string */
  value?: string;
  /** Callback when date changes - receives ISO string or null */
  onChange?: (value: string | null) => void;
  /** Callback when validation state changes */
  onValidationChange?: (isValid: boolean) => void;
  /** Callback when field loses focus */
  onBlur?: () => void;
  /** Error message to display */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Optional text or content to show below the field */
  suffixText?: ReactNode;
  /** Custom label for the field */
  label?: string;
  /** Custom className for the input field */
  className?: string;
  /** Custom className for the label */
  labelClassName?: string;
}

export const DateOfBirthField = ({
  value,
  onChange,
  onValidationChange,
  onBlur,
  error,
  disabled = false,
  suffixText,
  label = "Date of birth (optional)",
  className,
  labelClassName,
}: DateOfBirthFieldProps) => {
  // Track user input while typing (undefined means use computed display value)
  const [inputValue, setInputValue] = useState<string | undefined>();

  // Computed display value from prop
  const displayValue = inputValue ?? (value ? dayjs(value).format("L") : "");

  // Reset input value when parent value changes
  useEffect(() => {
    setInputValue(undefined);
  }, [value]);

  // Validate initial value
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      const currentYear = new Date().getFullYear();
      const dateYear = date.getFullYear();
      const isYearValid = dateYear >= 1900 && dateYear <= currentYear;
      onValidationChange?.(isYearValid);
    } else {
      onValidationChange?.(false);
    }
  }, [value, onValidationChange]);

  const handleChange = (inputValue: string) => {
    // Format the input as user types
    const formatted = formatToLongDate(inputValue);
    setInputValue(formatted);

    // Validate and call onChange if valid
    if (formatted.length >= 10 && isValidDateFormat(formatted)) {
      const isoDate = new Date(formatted).toISOString();
      const date = new Date(isoDate);
      const currentYear = new Date().getFullYear();
      const dateYear = date.getFullYear();

      // Validate year range: 1900 to current year
      const isYearValid = dateYear >= 1900 && dateYear <= currentYear;

      onChange?.(isoDate);
      onValidationChange?.(isYearValid);
    } else if (formatted === "") {
      // Handle empty input
      onChange?.(null);
      onValidationChange?.(false);
    } else {
      // For invalid/incomplete dates, mark as invalid
      onValidationChange?.(false);
    }
  };

  const handleBlur = () => {
    onBlur?.();
  };

  return (
    <div className="w-full">
      <TextField
        type="decimal"
        label={label}
        name="dateOfBirth"
        autoComplete="bday"
        inputMode="numeric"
        value={displayValue}
        maxLength={10}
        autoFocus
        placeholder={dayjs.localeData().longDateFormat("L")}
        pattern="[0-9]*"
        invalid={!!error}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        className={className}
        labelClassName={labelClassName}
      />
      {suffixText && (
        <div className="mb-1 mt-4 text-center text-body2 text-secondary">
          {suffixText}
        </div>
      )}
    </div>
  );
};
